import type { DataTableViewData } from '@shared/types/views';

/**
 * Sorting, searching and quick filters of a result table (layout lab,
 * interactions, 03/10/2026). Pure arrangement of the rows the table received:
 * nothing is stored, nothing is invented.
 */

type Row = DataTableViewData['rows'][number];
type Value = string | number | boolean | null;

/** Rows from which the table shows its search box and quick filters. */
export const INTERACTIVE_FROM_ROWS = 8;
/** A column is a quick filter when it has 2 to 6 short distinct values. */
const FILTER_MAX_VALUES = 6;
const FILTER_MAX_VALUE_LENGTH = 40;

const cellValue = (row: Row, key: string): Value => row.cells.find((cell) => cell.key === key)?.value ?? null;

/** « 18/12/2026 » or an ISO date, as a time; null otherwise. */
const asDate = (value: string): number | null => {
  const french = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (french) return new Date(Number(french[3]), Number(french[2]) - 1, Number(french[1])).getTime();
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
    const time = Date.parse(value);
    return Number.isNaN(time) ? null : time;
  }
  return null;
};

/** « 81 000 € », « 22 % », « 1,5 » as numbers; null when the text is not one number and its unit. */
const asNumber = (value: string): number | null => {
  const match = value.replace(/[\s\u00a0\u202f]/g, '').match(/^(-?\d+(?:[.,]\d+)?)[^\d.,]*$/);
  return match ? Number(match[1]!.replace(',', '.')) : null;
};

const collator = new Intl.Collator('fr-FR', { sensitivity: 'base', numeric: true });

/** Compares two cells: numbers, dates and text each by their nature; empty cells last. */
export const compareValues = (left: Value, right: Value): number => {
  const empty = (value: Value) => value === null || (typeof value === 'string' && value.trim() === '');
  if (empty(left) || empty(right)) return empty(left) === empty(right) ? 0 : empty(left) ? 1 : -1;
  if (typeof left === 'number' && typeof right === 'number') return left - right;
  const [a, b] = [String(left), String(right)];
  const [dateA, dateB] = [asDate(a), asDate(b)];
  if (dateA !== null && dateB !== null) return dateA - dateB;
  const [numberA, numberB] = [typeof left === 'number' ? left : asNumber(a), typeof right === 'number' ? right : asNumber(b)];
  if (numberA !== null && numberB !== null) return numberA - numberB;
  return collator.compare(a, b);
};

export interface TableQuery {
  sort: { key: string; direction: 'asc' | 'desc' } | null;
  search: string;
  filter: { key: string; value: string } | null;
}

const normalize = (text: string) => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

/** The value a quick filter compares: the text before « : » (« Écartée : B2C » → « Écartée »). */
export const filterLabel = (value: Value): string => String(value ?? '').split(/\s[:—-]\s|\s:\s?/)[0]!.trim();

export const applyTableQuery = (rows: Row[], query: TableQuery): Row[] => {
  const needle = normalize(query.search.trim());
  const filtered = rows.filter((row) =>
    (!needle || row.cells.some((cell) => cell.value !== null && normalize(String(cell.value)).includes(needle)))
    && (!query.filter || filterLabel(cellValue(row, query.filter.key)) === query.filter.value));
  if (!query.sort) return filtered;
  const { key, direction } = query.sort;
  const sign = direction === 'asc' ? 1 : -1;
  // Empty cells stay last whatever the direction.
  return [...filtered].sort((left, right) => {
    const [a, b] = [cellValue(left, key), cellValue(right, key)];
    const emptyA = a === null || a === '';
    const emptyB = b === null || b === '';
    if (emptyA || emptyB) return emptyA === emptyB ? 0 : emptyA ? 1 : -1;
    return sign * compareValues(a, b);
  });
};

/**
 * The column that makes the best quick filter: after the name column, the
 * first one whose values fall into 2 to 6 short groups. Null when none.
 */
export const quickFilterColumn = (data: DataTableViewData): { key: string; label: string; options: Array<{ value: string; count: number }> } | null => {
  if (data.rows.length < INTERACTIVE_FROM_ROWS) return null;
  for (const column of data.columns.slice(1)) {
    const counts = new Map<string, number>();
    for (const row of data.rows) {
      const value = filterLabel(cellValue(row, column.key));
      if (!value || value.length > FILTER_MAX_VALUE_LENGTH) continue;
      counts.set(value, (counts.get(value) ?? 0) + 1);
    }
    if (counts.size >= 2 && counts.size <= FILTER_MAX_VALUES && [...counts.values()].reduce((a, b) => a + b, 0) >= data.rows.length * 0.8) {
      return {
        key: column.key,
        label: column.label,
        options: [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([value, count]) => ({ value, count })),
      };
    }
  }
  return null;
};
